import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import {
  DefinitionDataSourceType,
  IRequirementDefinitionResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { useMutationCreateBackgroundCheckSettings } from "../../../../../query/backgroundChecks/mutations/useMutationCreateBackgroundCheckSettings";
import { useQueryBackgroundChecksPackages } from "../../../../../query/backgroundChecks/queries/useQueryBackgroundChecksPackages";
import { useFeatureFlags } from "../../../../../query/hooks/useFeatureFlags";
import { useConfirmCreateRequirementModal } from "../../../components/CreateRequirementDefinitionForm/useConfirmCreateRequirementModal";
import { selectorBackgroundChecksActivePackages } from "../../../../Requirements/selectors/selectorBackgroundChecksActivePackages";
import { CreateRequirementDefinitionFormValues } from "../../../../Requirements/types";
import { CreateRequirementDefinitionBackgroundChecksPartialForm } from "../../../../Requirements/modules/BackgroundChecks/types";
import { convertFormApprovalDataToReviewStrategy } from "../../../components/CreateRequirementDefinitionForm/utils";

type Params = {
  engagementId?: string;
  onSuccess: (requirement: IRequirementDefinitionResponse) => void;
};

export const useCreateBackgroundChecksRequirementDefinition = ({
  engagementId,
  onSuccess
}: Params) => {
  const { openSnackbar } = useWSSnackbar();

  const featureFlags = useFeatureFlags();

  const [createSettings, createSettingsMeta] =
    useMutationCreateBackgroundCheckSettings();

  const queryBackgroundChecks = useQueryBackgroundChecksPackages();

  const confirmCreateRequirementModal = useConfirmCreateRequirementModal();

  const onSubmit = async (
    formValues: CreateRequirementDefinitionFormValues &
      CreateRequirementDefinitionBackgroundChecksPartialForm
  ) => {
    const settings = await createSettings(
      {
        title: formValues.name,
        backgroundCheckPackageId: formValues.package,
        instructions: formValues.instructions
      },
      {
        throwOnError: true
      }
    );

    if (settings) {
      // open confirmation modal to create requirement
      await confirmCreateRequirementModal.open({
        engagementId,
        dataSourceType: DefinitionDataSourceType.BackgroundCheckRequestSettings,
        dataSourceId: settings.backgroundCheckRequestSettingsId,
        name: formValues.name,
        description: formValues.description,
        requirementType: RequirementType.BackgroundCheck,
        reviewStrategy: convertFormApprovalDataToReviewStrategy(formValues),
        onSuccess(requirement) {
          onSuccess?.(requirement);
        }
      });
    }
  };

  return {
    onSubmit,
    errors: createSettingsMeta.error,
    isLoading:
      createSettingsMeta.isLoading || queryBackgroundChecks.isInitialLoading,
    isEnabled: queryBackgroundChecks.data
      ? selectorBackgroundChecksActivePackages(queryBackgroundChecks.data)
          .length > 0
      : false
  };
};
